<script setup lang="ts">
    import {
        Select,
        SelectContent,
        SelectGroup,
        SelectItem,
        SelectLabel,
        SelectTrigger,
        SelectValue,
    } from '@/components/ui/select';

    import { SelectOption } from '@/types';
    import { useI18n } from 'vue-i18n';
    import { computed } from 'vue';

    const props = withDefaults(
        defineProps<{
            placeholder?: string;
            options: SelectOption[] | Record<string, any>[];
            translate?: boolean;
            valueBy?: string;
            labelBy?: string;
        }>(),
        {
            translate: false,
            valueBy: 'value',
            labelBy: 'label',
        },
    );

    const { t } = useI18n();

    const getOptionValue = (option: SelectOption | Record<string, any>): string => {
        return (option as Record<string, any>)[props.valueBy] ?? '';
    };

    const getOptionLabel = (option: SelectOption | Record<string, any>): string => {
        const label = (option as Record<string, any>)[props.labelBy];
        return props.translate ? t(label) : label;
    };

    const translatedOptions = computed(() =>
        props.options.map((option) => ({
            ...option,
            value: getOptionValue(option),
            label: getOptionLabel(option),
            addon: option.addon,
        })),
    );

    const placeholderWithDefault = props.placeholder || t('forms.select_placeholder');
</script>

<template>
    <Select>
        <SelectTrigger class="w-full">
            <SelectValue :placeholder="placeholderWithDefault" />
        </SelectTrigger>
        <SelectContent>
            <SelectGroup>
                <SelectItem
                    v-for="(option, index) in translatedOptions"
                    :key="index"
                    :value="option.value"
                >
                    {{ option.label }}
                </SelectItem>
            </SelectGroup>
        </SelectContent>
    </Select>
</template>
